import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mic, Calendar, Clock, CheckCircle2, Shield, Zap, ArrowRight, FileText, Play, Pause, RotateCcw, MessageSquare } from 'lucide-react';
import Footer from './Footer';
import MetallicStack from './MetallicStack';
import IntegrationHub from './IntegrationHub';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { APP_NAME } from '../config/branding';
import {
  trackAudioPlay,
  trackAudioComplete,
  trackAudioProgress,
  trackVideoPlay,
  trackCalculatorInteraction,
  trackFeatureCardClick,
  trackTranscriptView,
  trackGetStarted,
  trackDemoRequestEnhanced,
  trackPhoneTrialClick,
  trackOnlineTour,
  trackFaqClick
} from '../utils/analytics';

const dialogMessages: Record<string, string> = {
  'ai-phone': '24/7 AI phone agent handles calls, schedules appointments, and answers questions while your team focuses on patient care.',
  'outbound': 'Automatic appointment reminders and follow-up calls keep your schedule full and clients informed.',
  'calendar': 'Smart calendar syncs with your practice management system for seamless scheduling and automated reminders.',
  'website': 'Beautiful practice website with SEO-ready templates. See our Essential, Professional, and Enterprise plans on the Pricing page.',
  'iphone': 'Record visit audio on your iPhone and get AI-generated SOAP notes instantly. See plan details on the Pricing page.',
  'cost-savings': 'Replace multiple tools with one connected system for calls, booking, and visit notes. See plan details on the Pricing page.'
};

// Transcript data with timestamps (in seconds)
const transcript = [
  { time: 1, speaker: 'Agent', text: `Hi, um, this is LaShonda speaking from ${APP_NAME}. Thanks for calling. Could I get your phone number, please?` },
  { time: 6, speaker: 'User', text: "Yes. Four one five seven five four nine two one four." },
  { time: 12, speaker: 'Agent', text: "Thanks for that. Just to make sure I got it right-415-754-9214, correct?" },
  { time: 19, speaker: 'User', text: "Yep. That's it." },
  { time: 23, speaker: 'Agent', text: "Great! Welcome back, Justin. I see you have Amira and Winston in our system. Let's go ahead and set up an appointment-uh, which pet is this visit for?" },
  { time: 32, speaker: 'User', text: "Uh, this is for Winston." },
  { time: 36, speaker: 'Agent', text: "Alright, got it-this is for Winston. What day were you thinking of coming in? And do you have a" },
  { time: 41, speaker: 'User', text: "Fry" },
  { time: 40, speaker: 'Agent', text: "time of day that" },
  { time: 41, speaker: 'User', text: "Friday Friday, the nineteenth, and I'm thinking anytime on Friday, the nineteenth." },
  { time: 50, speaker: 'Agent', text: "So, um, we have two openings for Winston on Friday the 19th-one at 9:00 AM and another at 2:45 PM. Which one works better for you?" },
  { time: 59, speaker: 'User', text: "I'll take the two forty five please." },
  { time: 64, speaker: 'Agent', text: "Alright, Winston's all set for Friday the 19th at 2:45 PM. We're looking forward to seeing him! Is there anything else you need today?" },
  { time: 72, speaker: 'User', text: "Yeah. I'd like to check on my pet's meds." },
  { time: 76, speaker: 'Agent', text: "Sure thing! Are you looking to check on Winston's medications, Amira's, or both?" },
  { time: 81, speaker: 'User', text: "I'm, uh, Amiris." },
  { time: 84, speaker: 'Agent', text: "Oh, um, just so you know, this is a demo, so I can't actually check on medications or refills right now. Is there anything else I can help you with today?" },
  { time: 92, speaker: 'User', text: "No. Thank you. Goodbye." },
  { time: 96, speaker: 'Agent', text: "Alright, thank you so much, Justin. It was a pleasure serving you today. Have a great day-goodbye!" }
];

// Home page is primarily a marketing page with custom gradient designs
// Dark mode is applied to key structural elements while preserving gradient aesthetics
const Home = () => {
  // Revenue calculator state
  const [numDoctors, setNumDoctors] = useState(2);
  const [calculatorTab, setCalculatorTab] = useState<'no-shows' | 'upsell'>('no-shows');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set());
  const [activeDialog, setActiveDialog] = useState<string | null>(null);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isDashboardVisible, setIsDashboardVisible] = useState(true);
  const [isDashboardMaximized, setIsDashboardMaximized] = useState(false);
  const [email, setEmail] = useState('');

  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [currentTranscriptIndex, setCurrentTranscriptIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  // Tracking state for milestones
  const hasTracked50 = useRef(false);
  const hasTracked75 = useRef(false);
  const hasTrackedComplete = useRef(false);

  // Set page title for analytics
  usePageTitle(`${APP_NAME}: Manage Your Veterinary Practice Like a Boss`);

  // Track sales link clicks
  useSalesTracking();

  // Typewriter effect
  useEffect(() => {
    if (!activeDialog) {
      setDisplayedText('');
      return;
    }

    setIsTyping(true);
    setDisplayedText('');
    const fullText = dialogMessages[activeDialog];
    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 4); // 5ms per character for fast typing

    return () => clearInterval(typingInterval);
  }, [activeDialog]);

  const toggleFeature = (feature: string) => {
    setSelectedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(feature)) {
        // Deselecting - remove feature and clear dialog
        newSet.delete(feature);
        setActiveDialog(null);
      } else {
        // Selecting - add feature and show dialog
        newSet.add(feature);
        setActiveDialog(feature);
        // Track feature card click
        trackFeatureCardClick(feature);
      }
      return newSet;
    });
  };

  const faqs = [
    {
      question: 'How much revenue can I actually recover from no-shows?',
      answer: 'Based on industry data, practices with 10% no-show rates lose $75,000+ per vet annually. Our automated reminders reduce no-shows by 15%, recovering $11,250 per vet per year on average.'
    },
    {
      question: 'How do vaccine reminders increase revenue?',
      answer: 'Most practices only capture 40-60% of due vaccines. Our automated multi-channel reminders (SMS, email, voice) bring pets back for overdue vaccinations, adding $40k-$80k per vet in annual revenue.'
    },
    {
      question: 'How quickly will I see results?',
      answer: 'Most practices see reduced no-shows within the first week of enabling reminders. Revenue from vaccine campaigns typically shows within 30-60 days as overdue pets come in for care.'
    },
    {
      question: 'What makes your reminders more effective than what I already do?',
      answer: 'We use multi-channel delivery (SMS + email + voice), optimal timing based on data, and one-tap confirmation/rescheduling. Most practices see 2-3x better response rates than manual reminders.'
    },
    {
      question: 'Does this integrate with my practice management system?',
      answer: `Yes, ${APP_NAME} integrates with major practice management systems to automatically pull appointment data, vaccination schedules, and send reminders without any manual work from your team.`
    },
    {
      question: 'What does it cost vs. what I\'ll make?',
      answer: `${APP_NAME} is $500/month. A typical 2-vet practice recovers $22,500 from no-shows plus $120,000 from vaccine campaigns annually—a 20x+ return on investment.`
    },
  ];

  // Revenue Calculator Logic - No-Show Recovery (15% reduction model)
  // Assumptions: 5,000 appointments per doctor/year, $150 avg revenue, 10% baseline no-show rate
  const appointmentsPerDoctorPerYear = 5000;
  const avgRevenuePerAppointment = 150;
  const baselineNoShowRate = 0.10; // 10% industry average
  const reductionRate = 0.15; // 15% reduction in cancellations
  
  const totalAppointmentsPerYear = appointmentsPerDoctorPerYear * numDoctors;
  const baselineNoShows = Math.round(totalAppointmentsPerYear * baselineNoShowRate);
  const appointmentsRecovered = Math.round(baselineNoShows * reductionRate);
  const monthlySavings = Math.round((appointmentsRecovered * avgRevenuePerAppointment) / 12);
  const annualSavings = appointmentsRecovered * avgRevenuePerAppointment;

  // Revenue Gain Logic - Vaccine reminders & preventive upsells
  // Conservative-to-moderate lift: $40k-$80k per doctor/year
  const revenuePerDoctorLow = 40000;
  const revenuePerDoctorMid = 60000;
  const revenuePerDoctorHigh = 80000;
  
  const revenueLow = revenuePerDoctorLow * numDoctors;
  const revenueMid = revenuePerDoctorMid * numDoctors;
  const revenueHigh = revenuePerDoctorHigh * numDoctors;

  // Audio player controls
  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
      // Track audio play
      trackAudioPlay();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime;
      setCurrentTime(time);

      // Track listening milestones
      const percentage = (time / duration) * 100;
      if (percentage >= 50 && !hasTracked50.current) {
        hasTracked50.current = true;
        trackAudioProgress(50);
      }
      if (percentage >= 75 && !hasTracked75.current) {
        hasTracked75.current = true;
        trackAudioProgress(75);
      }
      if (percentage >= 95 && !hasTrackedComplete.current) {
        hasTrackedComplete.current = true;
        trackAudioComplete(duration);
      }

      // Find the current transcript line based on time
      let index = 0;
      for (let i = transcript.length - 1; i >= 0; i--) {
        if (time >= transcript[i].time) {
          index = i;
          break;
        }
      }
      setCurrentTranscriptIndex(index);

      // Auto-scroll to current line only if autoScroll is enabled
      // Scroll ONLY within the transcript container, not the whole page
      if (autoScroll && transcriptRef.current) {
        const container = transcriptRef.current;
        const activeElement = container.querySelector(`[data-index="${index}"]`) as HTMLElement;

        if (activeElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = activeElement.getBoundingClientRect();

          // Calculate the scroll position to center the element in the container
          const scrollTop = container.scrollTop;
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const centerPosition = elementTop - (containerRect.height / 2) + (elementRect.height / 2);

          // Smooth scroll within the container only
          container.scrollTo({
            top: centerPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  // Detect manual scrolling and disable auto-scroll
  const handleManualScroll = () => {
    if (autoScroll) {
      setAutoScroll(false);
    }

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-dark-bg-primary dark:to-dark-bg-secondary">
      {/* Hero Section - Clean Wave-style */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal text-[#1a2b4a] dark:text-dark-text-primary mb-6 leading-tight whitespace-nowrap" style={{
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
          }}>
            Manage your practice like a <span className="italic font-semibold" style={{ backgroundImage: 'url(/download.svg)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', padding: '0.1em 0.3em' }}>boss</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-dark-text-secondary mb-10 max-w-3xl mx-auto leading-relaxed" style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          }}>
            Operational clarity for veterinary groups. Scheduling, messaging, and insights across all your clinics.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/signup"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20"
              onClick={() => trackGetStarted('hero')}
            >
              Get {APP_NAME} Today
            </Link>
            <a
              href={`https://calendly.com/justin-helppet/30min${email ? `?email=${encodeURIComponent(email)}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-700 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 transition-all inline-flex items-center justify-center gap-2 border border-gray-200 dark:bg-dark-bg-secondary dark:border-dark-border dark:text-blue-400"
              onClick={() => trackDemoRequestEnhanced('hero', email)}
            >
              Request a demo
            </a>
          </div>

          {/* Hero Image */}
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-dark-border">
              <img
                src="/HP.png"
                alt={`${APP_NAME} Dashboard - Practice Management Software`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-4 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
              <div className="text-2xl font-bold text-gray-400">Google Calendar</div>
              <div className="text-2xl font-bold text-gray-400">Email Notifications</div>
              <div className="text-2xl font-bold text-gray-400">Text Messages</div>
              <div className="text-2xl font-bold text-gray-400">Push Notifications</div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Ecosystem Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-slate-50 dark:from-dark-bg-primary dark:to-dark-bg-secondary overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-6 tracking-tight">
            One Platform for Every Location
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-3xl mx-auto">
            Scheduling, messaging, reminders, and insights; unified across your entire veterinary group. One dashboard. One invoice. Zero guesswork.            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column: Integration Visualization */}
            <div className="relative">
              <IntegrationHub />
            </div>

            {/* Right Column: Product Stack */}
            <div className="relative">
              <MetallicStack standalone={false} />
            </div>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-dark-bg-secondary dark:via-dark-bg-primary dark:to-dark-bg-tertiary py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            }}>
              See {APP_NAME} in Action
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-3xl mx-auto">
              Watch how our AI Front Desk Agent handles calls, schedules appointments, and saves your team time
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-200">
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube-nocookie.com/embed/wwlhTnyl_Ik?rel=0&modestbranding=1"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title={`${APP_NAME} Demo Video`}
              />
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/signup"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-all inline-flex items-center justify-center gap-2"
              onClick={() => trackGetStarted('video_section')}
            >
              Get started today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>


      {/* Features Grid - Problem-Focused */}
      <section className="bg-gray-50 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Unified Calendar */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-8 text-white min-h-[280px] flex flex-col justify-between hover:shadow-2xl transition-shadow">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4 leading-tight" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}>
                  One Calendar for All Locations
                </h2>
                <p className="text-blue-100 text-lg leading-relaxed">
                  See every appointment across every clinic. No more calling between locations to check availability.
                </p>
              </div>
              <Link to="/products/calendar" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors inline-flex items-center gap-2 self-start mt-6">
                See the calendar →
              </Link>
            </div>

            {/* Online Booking */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-8 text-white min-h-[280px] flex flex-col justify-between hover:shadow-2xl transition-shadow">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4 leading-tight" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}>
                  Fewer Empty Exam Rooms
                </h2>
                <p className="text-green-100 text-lg leading-relaxed">
                  Online booking fills gaps in your schedule. Clients book 24/7 without calling. Works alongside your existing systems.
                </p>
              </div>
              <Link to="/products/online-booking" className="bg-white text-green-600 px-6 py-3 rounded-lg font-medium hover:bg-green-50 transition-colors inline-flex items-center gap-2 self-start mt-6">
                Reduce no-shows →
              </Link>
            </div>

            {/* 2-Way Texting */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-8 text-white min-h-[280px] flex flex-col justify-between hover:shadow-2xl transition-shadow">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4 leading-tight" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}>
                  Consistent Client Messaging
                </h2>
                <p className="text-teal-100 text-lg leading-relaxed">
                  Same reminders, same voice, same experience at every location. 2-way texting your clients actually respond to.
                </p>
              </div>
              <Link to="/products/two-way-texting" className="bg-white text-teal-600 px-6 py-3 rounded-lg font-medium hover:bg-teal-50 transition-colors inline-flex items-center gap-2 self-start mt-6">
                Unify messaging →
              </Link>
            </div>

            {/* Mobile Forms */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-8 text-white min-h-[280px] flex flex-col justify-between hover:shadow-2xl transition-shadow">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4 leading-tight" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}>
                  Digital Check-In
                </h2>
                <p className="text-orange-100 text-lg leading-relaxed">
                  Clients complete forms on their phone before arrival. Less paperwork, faster throughput, happier front desk.
                </p>
              </div>
              <Link to="/products/mobile-forms" className="bg-white text-orange-600 px-6 py-3 rounded-lg font-medium hover:bg-orange-50 transition-colors inline-flex items-center gap-2 self-start mt-6">
                Go paperless →
              </Link>
            </div>
          </div>

          {/* Problem-Focused Pills */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">No Replacement</h3>
              </div>
              <p className="text-sm text-gray-600">Works alongside your existing PMS</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold text-gray-900">One Dashboard</h3>
              </div>
              <p className="text-sm text-gray-600">Visibility across all locations</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-gray-900">One Invoice</h3>
              </div>
              <p className="text-sm text-gray-600">Per-location pricing, simple billing</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-teal-600" />
                <h3 className="font-semibold text-gray-900">Enhancement Layer</h3>
              </div>
              <p className="text-sm text-gray-600">Adds to your workflow, never replaces it</p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Calculator with Tabs */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setCalculatorTab('no-shows')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  calculatorTab === 'no-shows'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                No-Show Recovery
              </button>
              <button
                onClick={() => setCalculatorTab('upsell')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  calculatorTab === 'upsell'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Upsell Opportunities
              </button>
            </div>
          </div>

          {/* Calculator Card */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-10">
            
            {/* Slider Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Number of veterinarians</h3>
              <input
                type="range"
                min="1"
                max="10"
                value={numDoctors}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  setNumDoctors(newValue);
                  trackCalculatorInteraction(newValue);
                }}
                className={`w-full h-2 bg-gray-300 rounded-full appearance-none cursor-pointer ${calculatorTab === 'no-shows' ? 'accent-blue-500' : 'accent-green-500'}`}
                style={{
                  background: `linear-gradient(to right, ${calculatorTab === 'no-shows' ? '#3b82f6' : '#22c55e'} 0%, ${calculatorTab === 'no-shows' ? '#3b82f6' : '#22c55e'} ${((numDoctors - 1) / 9) * 100}%, #d1d5db ${((numDoctors - 1) / 9) * 100}%, #d1d5db 100%)`
                }}
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>1</span>
                <span className={`text-4xl font-semibold -mt-2 ${calculatorTab === 'no-shows' ? 'text-blue-600' : 'text-green-600'}`}>{numDoctors}</span>
                <span>10</span>
              </div>
            </div>

            {/* No-Show Recovery Results */}
            {calculatorTab === 'no-shows' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Appointments Recovered</p>
                  <p className="text-4xl font-bold text-gray-900">{appointmentsRecovered}</p>
                  <p className="text-sm text-green-600 mt-1">15% reduction</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Monthly Savings</p>
                  <p className="text-4xl font-bold text-gray-900">${monthlySavings.toLocaleString()}</p>
                  <p className="text-sm text-gray-400 mt-1">~{appointmentsRecovered} appts/year</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Annual Savings</p>
                  <p className="text-4xl font-bold text-gray-900">${annualSavings.toLocaleString()}</p>
                  <p className="text-sm text-gray-400 mt-1">At $150/visit</p>
                </div>
              </div>
            )}

            {/* Upsell Opportunities Results */}
            {calculatorTab === 'upsell' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Conservative</p>
                  <p className="text-4xl font-bold text-gray-900">${(revenueLow / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-gray-400 mt-1">$40k/doctor</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-green-200 bg-green-50">
                  <p className="text-sm text-green-600 font-medium mb-2">Moderate</p>
                  <p className="text-4xl font-bold text-green-600">${(revenueMid / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-gray-400 mt-1">$60k/doctor</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">High (Defensible)</p>
                  <p className="text-4xl font-bold text-gray-900">${(revenueHigh / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-gray-400 mt-1">$80k/doctor</p>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-400 text-center mt-6">
            {calculatorTab === 'no-shows' 
              ? 'Based on 5,000 appointments per vet/year, 10% no-show rate, and $150 average visit'
              : 'Based on vaccine reminders, preventive care upsells, and wellness plan conversions'
            }
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 dark:bg-dark-bg-primary py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary text-center" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            }}>
              Frequently asked questions
            </h2>
          </div>
          <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-border">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-100 dark:border-dark-border last:border-b-0">
                <button
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors"
                  onClick={() => {
                    setExpandedFaq(expandedFaq === index ? null : index);
                    if (expandedFaq !== index) {
                      trackFaqClick(faq.question);
                    }
                  }}
                >
                  <span className="text-lg font-medium text-black dark:text-dark-text-primary pr-8 leading-relaxed" style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                  }}>
                    {faq.question}
                  </span>
                  <ArrowRight
                    className={`w-6 h-6 text-gray-400 dark:text-dark-text-muted transition-transform flex-shrink-0 ${expandedFaq === index ? 'rotate-90' : ''
                      }`}
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-dark-text-secondary">
                    <p className="text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
            Recover lost revenue and<br />
            grow your practice
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join veterinary practices recovering $100k+ annually with automated reminders and upsell campaigns
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link
              to="/signup"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              onClick={() => trackGetStarted('bottom_cta')}
            >
              Start Growing Revenue
            </Link>
            <a
              href="tel:419-283-1624"
              className="bg-blue-800 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-blue-900 transition-colors inline-flex items-center justify-center gap-2"
              onClick={() => trackPhoneTrialClick()}
            >
              <Phone className="w-5 h-5" />
              Talk to Sales
            </a>
          </div>
          <p className="text-sm text-blue-200">No-Show Recovery • Vaccine Reminders • Preventive Care Campaigns</p>
        </div>
      </section>

      <Footer />
    </div >
  );
};

export default Home;